import { Menu } from '@headlessui/react'
import {
  ArrowRightIcon,
  ChevronDownIcon,
  RocketLaunchIcon,
} from '@heroicons/react/20/solid'
import { SetupFooter } from 'components/SetupFooter'
import { SetupLayout, buttonClass } from 'components/SetupLayout'
import { useRewardful } from 'hooks/util/useRewardful'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { requireAuth, useAuth } from 'util/auth'
import { classNames } from 'util/classNames'
import { PLANS } from '../../lib/types/pricing-table.config'
import { redirectToCheckout } from 'util/stripe'
import Sentry from 'util/sentry'
import { PricingTableModal } from 'components/PricingTableModal'
import { Button } from 'components/Button'
import { useProjectStore } from 'store/useProjectStore'
import { useUser } from 'util/db'
import { usePopper } from 'react-popper'

function Billing() {
  return (
    <SetupLayout>
      <StartFreeTrial />
    </SetupLayout>
  )
}

export default requireAuth(Billing)

export const StartFreeTrial = () => {
  const { referral } = useRewardful()
  const [selectedPlan, setSelectedPlan] = useState<{
    name: string
    id: string
  }>({
    name: 'Base',
    id: 'base',
  })
  const [isYearly, setIsYearly] = useState<boolean>(false)
  const router = useRouter()
  const [showPromoCode, setShowPromoCode] = useState<boolean>(false)
  const [promoCode, setPromoCode] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const startTrial = () => {
    setLoading(true)
    redirectToCheckout({
      planId: selectedPlan.id,
      quantity: 1,
      clientReferenceId: referral,
      trial: true,
      cancelUrl: `${process.env['NEXT_PUBLIC_BASE_URL']}/setup/billing`,
      redirectUrl: `${process.env['NEXT_PUBLIC_BASE_URL']}/setup/project?plan=${selectedPlan.id}`,
      replaceHistoryPage: router.asPath,
    })
      .catch((error) => {
        Sentry.captureException(error)
        toast.error('Error starting trial')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const [isPromoLoading, setIsPromoLoading] = useState<boolean>(false)

  const handleApplyPromoCode = () => {
    setIsPromoLoading(true)
    setTimeout(() => {
      // For now, just show an invalid promo code toast
      toast.error('Invalid promo code')
      // Clear the input field after submission
      setPromoCode('')
      setIsPromoLoading(false)
    }, 1000)
  }

  const handlePromoCodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleApplyPromoCode()
    }
  }

  return (
    <>
      <div className="flex w-full flex-row justify-between gap-x-2 gap-y-2 sm:items-center">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-x-2">
            <RocketLaunchIcon className="h-5 w-5 text-toasty-blue-500" />
            <h1 className="text-xl font-semibold text-slate-900">
              Start free trial
            </h1>
          </div>
        </div>
        <SelectPlan
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          isYearly={isYearly}
          setIsYearly={setIsYearly}
        />
      </div>
      <div className="mb-4 flex flex-col gap-y-2">
        <p className="text-slate-700">
          Pay $0 today, cancel your trial anytime within 7 days — no questions
          asked.
        </p>
      </div>
      <Button
        onClick={startTrial}
        disabled={loading}
        className={buttonClass}
        loading={loading}
        icon={ArrowRightIcon}
        size="lg"
        iconPosition="right"
      >
        Start free trial
      </Button>

      <div className="mt-2">
        {!showPromoCode ? (
          <button
            onClick={() => setShowPromoCode(true)}
            className="text-sm font-medium text-toasty-blue-600 hover:font-bold hover:text-toasty-blue-700"
          >
            Have a promo code? <span className="underline">Click here</span>
          </button>
        ) : (
          <div className="flex w-full max-w-md items-end gap-1">
            <div className="flex-grow">
              <label className="text-sm font-medium text-slate-700">
                Promo code
              </label>
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                onKeyDown={handlePromoCodeKeyDown}
                placeholder="Enter promo code"
                className="!h-10 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-toasty-blue-500 focus:outline-none focus:ring-1 focus:ring-toasty-blue-500"
              />
            </div>
            <Button
              onClick={handleApplyPromoCode}
              size="lg"
              className="!h-10"
              variant="solid"
              color="blue"
              loading={isPromoLoading}
              icon={ArrowRightIcon}
              iconPosition="right"
            >
              Apply
            </Button>
          </div>
        )}
      </div>

      <SetupFooter
        title="Why do I need to enter payment details?"
        items={[
          'We collect payment information up front to prevent abuse.',
          'You can cancel your 7-day free trial at any time within your account settings.',
        ]}
      />
    </>
  )
}

const SelectPlan = ({
  selectedPlan,
  setSelectedPlan,
  isYearly,
  setIsYearly,
}) => {
  const { showPricingModal, setShowPricingModal } = useProjectStore()
  const [referenceElement, setReferenceElement] =
    useState<HTMLButtonElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  )

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
          boundary: document.body,
        },
      },
    ],
  })

  return (
    <>
      {showPricingModal && (
        <PricingTableModal
          show="all"
          onSelectPlan={({ id, name }) => {
            setSelectedPlan({ id, name })
            setIsYearly(id.toLowerCase().includes('yearly'))
            setShowPricingModal(false)
          }}
          showPricingModal={showPricingModal}
          setShowPricingModal={setShowPricingModal}
          isLoading={false}
        />
      )}
      <div className="flex flex-col items-center">
        <div className="w-full max-w-lg">
          <Menu as="div" className="relative inline-block w-full text-left">
            {({ open, close }) => (
              <>
                <Menu.Button
                  ref={setReferenceElement}
                  className={classNames(
                    'flex items-center gap-x-1 truncate rounded-md !bg-white px-2 py-2 text-xs font-bold text-slate-800 hover:bg-slate-100'
                  )}
                >
                  <span className="font-medium">
                    Selected plan: {selectedPlan.name}
                  </span>
                  <span>
                    <ChevronDownIcon className="h-5 w-5" />
                  </span>
                </Menu.Button>

                <Menu.Items
                  ref={setPopperElement}
                  style={styles.popper}
                  {...attributes.popper}
                  className="z-50 w-[min(400px,calc(100vw-2rem))] rounded-lg bg-white p-4 shadow-lg ring-1 ring-black ring-opacity-5"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-lg font-semibold text-slate-800">
                      Select plan
                    </h2>
                    <button
                      className="flex items-center gap-x-1 text-sm font-medium text-toasty-blue-600 hover:underline"
                      onClick={() => setIsYearly(!isYearly)}
                    >
                      {isYearly
                        ? 'See Monthly Options'
                        : 'Save 50% with Annual'}
                      <ArrowRightIcon className="h-4 w-4" />
                    </button>
                  </div>

                  {PLANS.filter((plan) =>
                    isYearly
                      ? plan.billingCycle === 'Annual'
                      : plan.billingCycle === 'Monthly'
                  ).map((plan) => {
                    return (
                      <Menu.Item key={plan.id}>
                        {({ active }) => (
                          <div
                            className={`mb-2 cursor-pointer rounded-lg border-2 px-4 py-2 ${
                              active
                                ? 'border-2 border-slate-400 bg-slate-100'
                                : ''
                            } ${
                              selectedPlan.name === plan.name
                                ? 'border-slate-400 bg-slate-100'
                                : ''
                            }`}
                            onClick={() => {
                              setSelectedPlan({
                                name: plan.name,
                                id: plan.id,
                              })
                              close()
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="text-lg font-semibold text-slate-800">
                                    {plan.name}
                                  </h3>
                                </div>
                                <div className="flex flex-col gap-y-1">
                                  <div className="flex items-baseline gap-2">
                                    <span className="text-xl font-bold text-slate-700">
                                      {plan.price}
                                    </span>
                                    {plan.originalPrice && (
                                      <span className="text-sm font-medium text-slate-500 line-through">
                                        {plan.originalPrice}
                                      </span>
                                    )}
                                    <span className="ml-1 font-semibold text-slate-700">
                                      per month{' '}
                                      {isYearly && '(billed annually)'}
                                    </span>
                                  </div>
                                  <p className="text-sm tracking-tight text-slate-700">
                                    {plan.condensedDescription}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Menu.Item>
                    )
                  })}

                  <div className="mt-2 flex w-full items-center justify-center border-t border-slate-300 pt-4">
                    <button
                      onClick={() => {
                        setShowPricingModal(true)
                        close()
                      }}
                      className="text-toasty-blue-600 hover:underline"
                    >
                      Compare features
                    </button>
                  </div>
                </Menu.Items>
              </>
            )}
          </Menu>
        </div>
      </div>
    </>
  )
}
